import { ChevronLeft, ChevronRight } from "lucide-react";
import { MicrosoftCalendarWeek } from "./MicrosoftCalendarWeek";
import { useState } from "react";
import { useQuery, skipToken } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { MicrosoftAccountKind, MicrosoftCalendarViewDocument } from "@/graphql/generated/operations";
import { Button } from "@/components/design/Button";
import { DatePicker } from "@/components/design/DatePicker";
import { calendarWeekWindow, localCalendarDate } from "@/lib/microsoft-calendar-time";
import { userErrorMessage } from "@/lib/user-error";

interface Props { accountKind?: MicrosoftAccountKind; organizer: string; start: string; end: string }
function today() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
export function MicrosoftCalendarAgenda({ accountKind = MicrosoftAccountKind.Personal, organizer, start, end }: Props) {
  const intl = useIntl(); const t = (id: string) => intl.formatMessage({ id: `microsoft.agenda.${id}` });
  const [enabled, setEnabled] = useState(false);
  const anchor = start.slice(0, 10) || today();
  const [selection, setSelection] = useState<{ anchor: string; day: string } | null>(null);
  const day = selection?.anchor === anchor ? selection.day : anchor;
  const window = calendarWeekWindow(day);
  const { data, loading, error, refetch } = useQuery(MicrosoftCalendarViewDocument, enabled && window ? {
    variables: { accountKind, organizerEmail: organizer, start: window.start, end: window.end }, fetchPolicy: "no-cache", notifyOnNetworkStatusChange: true,
  } : skipToken);
  const received = data?.microsoftCalendarView;
  const view = !loading && !error && received?.organizerEmail === organizer
    && window && new Date(received.start).getTime() === new Date(window.start).getTime()
    && new Date(received.end).getTime() === new Date(window.end).getTime() ? received : null;
  const selectedStart = new Date(start).getTime(); const selectedEnd = new Date(end).getTime();
  const validSelection = Number.isFinite(selectedStart) && selectedEnd > selectedStart;
  const overlaps = (event: { start: string; end: string; showAs: string }) => validSelection
    && !["free", "workingElsewhere"].includes(event.showAs)
    && new Date(event.start).getTime() < selectedEnd && new Date(event.end).getTime() > selectedStart;
  const conflicts = view?.events.filter(overlaps).length ?? 0;
  const selectionTouchesWeek = validSelection && window && selectedStart < new Date(window.end).getTime()
    && selectedEnd > new Date(window.start).getTime();
  function moveWeek(amount: number) {
    const next = new Date(`${day}T12:00:00`); next.setDate(next.getDate() + amount * 7);
    setSelection({ anchor, day: localCalendarDate(next) });
  }

  return <section className="space-y-4 border-t border-ink/10 pt-4" aria-label={t("availability")}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h4 className="text-smycles-body font-medium">{t("availability")}</h4>
      <p className="text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "microsoft.agenda.account" }, { email: organizer })}</p>
    </div>
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <div className="flex items-center gap-1">
        <Button id="microsoft-agenda-prev" variant="icon" size="square" aria-label={t("previousWeek")} disabled={!window} onClick={() => moveWeek(-1)}><ChevronLeft size={18} /></Button>
        {window ? <h5 className="text-smycles-body font-medium">{intl.formatDate(`${window.days[0]}T12:00`, { day: "numeric", month: "short" })} – {intl.formatDate(`${window.days[6]}T12:00`, { day: "numeric", month: "short", year: "numeric" })}</h5> : null}
        <Button id="microsoft-agenda-next" variant="icon" size="square" aria-label={t("nextWeek")} disabled={!window} onClick={() => moveWeek(1)}><ChevronRight size={18} /></Button>
        <DatePicker id="microsoft-agenda-day" aria-label={t("title")} triggerVariant="icon" required value={day}
          onChange={next => setSelection({ anchor, day: next })} />
      </div>
      <div className="flex items-center gap-2">
        <Button id="microsoft-agenda-today" variant="secondary" size="compact" onClick={() => setSelection({ anchor, day: today() })}>{t("today")}</Button>
        {enabled ? <Button id="microsoft-agenda-load" type="button" variant="secondary" size="compact" disabled={loading || !window}
          onClick={() => void refetch()}>{t("refresh")}</Button> : null}
      </div>
    </div>
    {!enabled ? <div className="flex flex-col items-center gap-3 rounded-lg bg-ink/5 px-4 py-6 text-center">
      <p className="text-smycles-body text-ink-muted">{t("notLoaded")}</p>
      <Button id="microsoft-agenda-load" type="button" variant="secondary" size="compact" disabled={!window}
        onClick={() => setEnabled(true)}>{t("load")}</Button>
    </div> : null}
    {loading ? <p role="status" className="text-smycles-body">{t("loading")}</p> : null}
    {error ? <p role="alert" className="text-smycles-body">{userErrorMessage(intl, error)}</p> : null}
    {view ? <div className="space-y-4">
      {view.truncated ? <p role="status" className="text-smycles-body">{t("truncated")}</p> : null}
      {selectionTouchesWeek ? <p role="status" className="text-smycles-body font-medium">{conflicts > 0
        ? intl.formatMessage({ id: "microsoft.agenda.conflicts" }, { count: conflicts })
        : !view.truncated ? t("noConflicts") : null}</p> : null}
      {window ? <MicrosoftCalendarWeek days={window.days} events={view.events} start={start} end={end} overlaps={overlaps} /> : null}
      {view.events.length === 0 ? <p className="text-smycles-meta text-ink-muted">{t("empty")}</p> : null}
      <p className="text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "microsoft.agenda.checked" }, {
        time: intl.formatTime(view.fetchedAt), zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })}</p>
    </div> : null}
  </section>;
}
